import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CONTACTS_QUERY_KEY, createContact, editContact } from './CarrierProfile.service'
import { type Contact, type ContactFormValues, ContactRole } from './CarrierProfile.types'

interface CreateOrEditContactDialogPayload {
  readonly contact: Contact
}

export interface CreateOrEditContactDialogProps {
  readonly payload?: CreateOrEditContactDialogPayload
  readonly open: boolean
  readonly onAction: (contact: Contact) => void
  readonly onClose: () => void
}

const CreateOrEditContactDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditContactDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<ContactFormValues>()
  const queryClient = useQueryClient()
  const createContactMutation = useMutation({
    mutationFn: createContact,
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] }),
  })
  const updateContactMutation = useMutation({
    mutationFn: (data: ContactFormValues) => editContact(payload?.contact.id ?? '', data),
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          name: payload?.contact.name,
          roles: payload?.contact.roles ?? [],
          location: payload?.contact.location,
          email: payload?.contact.email,
          phone: payload?.contact.phone,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<ContactFormValues> = async (data: ContactFormValues): Promise<void> => {
    if (payload) {
      onAction(await updateContactMutation.mutateAsync(data))
      analyticsEvent('polestar_carrier_profile_contact_updated')
    } else {
      onAction(await createContactMutation.mutateAsync(data))
      analyticsEvent('polestar_carrier_profile_contact_created')
    }
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={payload ? t('screens.cs.carrier_profile.contacts.edit_contact') : t('screens.cs.carrier_profile.contacts.add_contact')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormText
          name="name"
          label={t('screens.cs.carrier_profile.contacts.name')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormDropdown
          name="roles"
          label={t('screens.cs.carrier_profile.contacts.roles')}
          control={control}
          options={Object.values(ContactRole).map((role) => ({
            value: role,
            label: t(`screens.cs.carrier_profile.contacts.${role}`),
          }))}
          rules={{
            required: t('form.validation.required'),
          }}
          multiple
        />
        <KNFormText
          name="location"
          label={t('screens.cs.carrier_profile.contacts.location')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormText
          name="email"
          label={t('screens.cs.carrier_profile.contacts.email')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormText
          name="phone"
          label={t('screens.cs.carrier_profile.contacts.phone')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
      </Stack>
    </KNDialog>
  )
}

export default CreateOrEditContactDialog
